import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Monitor, Smartphone, Tablet, Globe, X, LogOut, Loader2, ShieldCheck,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { toast } from 'sonner';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';

/**
 * Lightweight parser that extracts a readable device + browser/app label
 * from a raw User-Agent string. Matches the most common Slimmy clients
 * (Capacitor iOS/Android + desktop browsers) without pulling in a full
 * UA library.
 */
function parseUserAgent(ua) {
  if (!ua) return { label: 'Dispositivo sconosciuto', Icon: Globe };
  const s = ua.toLowerCase();

  let Icon = Monitor;
  let device = 'Computer';
  if (/ipad/.test(s)) { Icon = Tablet; device = 'iPad'; }
  else if (/iphone/.test(s) || /ios/.test(s)) { Icon = Smartphone; device = 'iPhone'; }
  else if (/android/.test(s)) { Icon = Smartphone; device = /mobile/.test(s) ? 'Android' : 'Android Tablet'; }
  else if (/macintosh|mac os/.test(s)) { device = 'Mac'; }
  else if (/windows/.test(s)) { device = 'Windows'; }
  else if (/linux/.test(s)) { device = 'Linux'; }

  let browser = '';
  if (/slimmy/.test(s)) browser = 'App Slimmy';
  else if (/edg\//.test(s)) browser = 'Edge';
  else if (/chrome\//.test(s) && !/edg\//.test(s)) browser = 'Chrome';
  else if (/firefox/.test(s)) browser = 'Firefox';
  else if (/safari/.test(s) && !/chrome/.test(s)) browser = 'Safari';
  else if (/postman|curl|httpie|python/.test(s)) browser = 'API client';

  const label = browser ? `${device} · ${browser}` : device;
  return { label, Icon };
}

function formatRelative(iso, t) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t('sessions.justNow', 'Adesso');
  if (m < 60) return t('sessions.minutesAgo', '{{n}} min fa', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('sessions.hoursAgo', '{{n}} ore fa', { n: h });
  const d = Math.floor(h / 24);
  if (d < 30) return t('sessions.daysAgo', '{{n}} giorni fa', { n: d });
  return new Date(iso).toLocaleDateString();
}

export default function ActiveSessionsButton() {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [revokingId, setRevokingId] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpClient.get(`${API_URL}/api/users/me/sessions`);
      setSessions(res.data?.sessions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('sessions.loadError', 'Impossibile caricare le sessioni'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const revokeOne = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      await httpClient.delete(`${API_URL}/api/users/me/sessions/${sessionId}`);
      toast.success(t('sessions.revoked', 'Sessione revocata'));
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('sessions.revokeError', 'Errore'));
    } finally {
      setRevokingId(null);
    }
  };

  const revokeAll = async () => {
    setRevokingId('all');
    try {
      const res = await httpClient.delete(`${API_URL}/api/users/me/sessions`);
      toast.success(t('sessions.revokedMany', 'Revocate {{n}} sessioni', { n: res.data?.revoked || 0 }));
      setConfirmAll(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('sessions.revokeError', 'Errore'));
    } finally {
      setRevokingId(null);
    }
  };

  const close = () => { if (!revokingId) { setOpen(false); setConfirmAll(false); } };
  const otherCount = sessions.filter((s) => !s.is_current).length;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`w-full glass-card-sm p-4 flex items-center gap-3 transition-colors ${
          isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-50'
        }`}
        data-testid="active-sessions-btn"
      >
        <ShieldCheck className="w-5 h-5 opacity-70" />
        <div className="flex-1 text-left">
          <div className="font-medium">{t('sessions.title', 'Dispositivi attivi')}</div>
          <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {t('sessions.desc', 'Controlla e revoca le sessioni su altri dispositivi')}
          </div>
        </div>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
          onClick={close}
          data-testid="active-sessions-overlay"
        >
          <div
            className={`w-full max-w-md rounded-3xl overflow-hidden shadow-2xl flex flex-col ${
              isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            style={{ maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-5 py-4 flex items-center justify-between border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('sessions.title', 'Dispositivi attivi')}
              </div>
              <button
                type="button"
                onClick={close}
                className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                data-testid="active-sessions-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {loading ? (
                <div className="py-10 flex justify-center">
                  <Loader2 className={`w-6 h-6 animate-spin ${isDark ? 'text-white/60' : 'text-gray-400'}`} />
                </div>
              ) : sessions.length === 0 ? (
                <p className={`text-center text-sm py-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t('sessions.empty', 'Nessuna sessione attiva')}
                </p>
              ) : (
                sessions.map((s) => {
                  const { label, Icon } = parseUserAgent(s.user_agent);
                  return (
                    <div
                      key={s.session_id}
                      className={`p-3 rounded-2xl flex items-center gap-3 ${
                        s.is_current
                          ? (isDark ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200')
                          : (isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200')
                      }`}
                      data-testid={`session-row-${s.session_id}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        s.is_current
                          ? 'bg-emerald-500/20 text-emerald-500'
                          : (isDark ? 'bg-white/10 text-white/70' : 'bg-white text-gray-600')
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {label}
                          {s.is_current && (
                            <span className="ml-2 inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-500 text-white">
                              {t('sessions.current', 'Questo dispositivo')}
                            </span>
                          )}
                        </div>
                        <div className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                          {s.ip_masked && <>{s.ip_masked} · </>}
                          {formatRelative(s.last_seen_at, t)}
                        </div>
                      </div>
                      {!s.is_current && (
                        <button
                          onClick={() => revokeOne(s.session_id)}
                          disabled={revokingId === s.session_id}
                          className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                            isDark
                              ? 'text-red-400 hover:bg-red-500/10'
                              : 'text-red-500 hover:bg-red-50'
                          } disabled:opacity-50`}
                          title={t('sessions.revoke', 'Disconnetti')}
                          data-testid={`revoke-session-${s.session_id}`}
                        >
                          {revokingId === s.session_id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <LogOut className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {otherCount > 0 && (
              <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                {!confirmAll ? (
                  <button
                    type="button"
                    onClick={() => setConfirmAll(true)}
                    className={`w-full py-3 rounded-xl font-medium text-sm transition-colors ${
                      isDark
                        ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20'
                        : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                    }`}
                    data-testid="revoke-all-btn"
                  >
                    {t('sessions.revokeAll', 'Disconnetti tutti gli altri dispositivi')} ({otherCount})
                  </button>
                ) : (
                  <div className="space-y-2">
                    <p className={`text-xs text-center ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      {t('sessions.revokeAllConfirm', 'Sei sicuro? Tutte le altre sessioni verranno chiuse.')}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmAll(false)}
                        disabled={revokingId === 'all'}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'}`}
                      >
                        {t('common.cancel', 'Annulla')}
                      </button>
                      <button
                        type="button"
                        onClick={revokeAll}
                        disabled={revokingId === 'all'}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-500 hover:bg-red-600 text-white flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="revoke-all-confirm"
                      >
                        {revokingId === 'all'
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <>{t('sessions.revokeAllAction', 'Disconnetti')}</>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
